import { useState, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { ArrowLeft, Mail, Phone, Lock, CheckCircle, Loader2, Eye, EyeOff, Check, X, KeyRound } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useLanguage } from '../context/LanguageContext';
import { useTheme } from '../context/ThemeContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function ForgotPassword() {
  const { language } = useLanguage();
  const { isDarkMode } = useTheme();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    emailCode: '',
    phoneCode: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [maskedPhone, setMaskedPhone] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const navigate = useNavigate();

  const emailOtpRefs = useRef([]);
  const phoneOtpRefs = useRef([]);

  // Password validation
  const passwordRequirements = {
    minLength: formData.newPassword.length >= 10,
    hasUppercase: /[A-Z]/.test(formData.newPassword),
    hasLowercase: /[a-z]/.test(formData.newPassword),
    hasNumber: /[0-9]/.test(formData.newPassword),
    hasSymbol: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(formData.newPassword)
  };

  const isPasswordValid = Object.values(passwordRequirements).every(Boolean);
  const doPasswordsMatch = formData.newPassword === formData.confirmPassword && formData.confirmPassword.length > 0;

  const handleOtpChange = (index, value, type) => {
    if (value && !/^\d$/.test(value.slice(-1))) return;
    
    const digit = value ? value.slice(-1) : '';
    
    if (type === 'email') {
      setFormData(prev => {
        const codeArray = prev.emailCode.padEnd(6, ' ').split('');
        codeArray[index] = digit;
        return { ...prev, emailCode: codeArray.join('').replace(/ /g, '') };
      });
      
      if (digit && index < 5) {
        setTimeout(() => emailOtpRefs.current[index + 1]?.focus(), 10);
      }
    } else {
      setFormData(prev => {
        const codeArray = prev.phoneCode.padEnd(6, ' ').split('');
        codeArray[index] = digit;
        return { ...prev, phoneCode: codeArray.join('').replace(/ /g, '') };
      });
      
      if (digit && index < 5) {
        setTimeout(() => phoneOtpRefs.current[index + 1]?.focus(), 10);
      }
    }
  };

  const handleOtpKeyDown = (index, e, type) => {
    const refs = type === 'email' ? emailOtpRefs : phoneOtpRefs;
    
    if (e.key === 'Backspace') {
      e.preventDefault();
      if (type === 'email') {
        setFormData(prev => {
          const codeArray = prev.emailCode.padEnd(6, ' ').split('');
          if (codeArray[index] && codeArray[index] !== ' ') {
            codeArray[index] = ' ';
          } else if (index > 0) {
            codeArray[index - 1] = ' ';
            setTimeout(() => refs.current[index - 1]?.focus(), 10);
          }
          return { ...prev, emailCode: codeArray.join('').replace(/ /g, '') };
        });
      } else {
        setFormData(prev => {
          const codeArray = prev.phoneCode.padEnd(6, ' ').split('');
          if (codeArray[index] && codeArray[index] !== ' ') {
            codeArray[index] = ' ';
          } else if (index > 0) {
            codeArray[index - 1] = ' ';
            setTimeout(() => refs.current[index - 1]?.focus(), 10);
          }
          return { ...prev, phoneCode: codeArray.join('').replace(/ /g, '') };
        });
      }
    }
  };

  // Handle paste for OTP
  const handleOtpPaste = (e, type) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    
    if (pastedData.length > 0) {
      if (type === 'email') {
        setFormData(prev => ({ ...prev, emailCode: pastedData }));
        // Focus on the last filled input or the next empty one
        const nextIndex = Math.min(pastedData.length, 5);
        setTimeout(() => emailOtpRefs.current[nextIndex]?.focus(), 10);
      } else {
        setFormData(prev => ({ ...prev, phoneCode: pastedData }));
        const nextIndex = Math.min(pastedData.length, 5);
        setTimeout(() => phoneOtpRefs.current[nextIndex]?.focus(), 10);
      }
    }
  };

  // Step 1: Send email code
  const sendEmailCode = async () => {
    if (!formData.email) {
      toast.error(language === 'ar' ? 'يرجى إدخال البريد الإلكتروني' : 'Please enter your email');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${BACKEND_URL}/api/auth/forgot-password/send-email-code`, {
        email: formData.email
      });
      toast.success(language === 'ar' ? 'تم إرسال رمز التحقق إلى بريدك' : 'Verification code sent to your email');
      setStep(2);
    } catch (error) {
      toast.error(error.response?.data?.detail || (language === 'ar' ? 'لا يوجد حساب بهذا البريد' : 'No account found with this email'));
    } finally {
      setLoading(false);
    }
  };

  // Step 2: Verify email code
  const verifyEmailCode = async () => {
    if (formData.emailCode.length !== 6) {
      toast.error(language === 'ar' ? 'يرجى إدخال الرمز المكون من 6 أرقام' : 'Please enter the 6-digit code');
      return;
    }
    setLoading(true);
    try {
      const response = await axios.post(`${BACKEND_URL}/api/auth/forgot-password/verify-email`, {
        email: formData.email,
        code: formData.emailCode
      });
      setMaskedPhone(response.data.masked_phone);
      toast.success(language === 'ar' ? 'تم التحقق من البريد الإلكتروني' : 'Email verified successfully');
      setStep(3);
    } catch (error) {
      toast.error(error.response?.data?.detail || (language === 'ar' ? 'رمز التحقق غير صحيح' : 'Invalid verification code'));
    } finally {
      setLoading(false);
    }
  };

  // Step 3: Send phone code
  const sendPhoneCode = async () => {
    setLoading(true);
    try {
      await axios.post(`${BACKEND_URL}/api/auth/forgot-password/send-phone-code`, {
        email: formData.email
      });
      toast.success(language === 'ar' ? 'تم إرسال رمز التحقق إلى هاتفك' : 'Verification code sent to your phone');
      setStep(4);
    } catch (error) {
      toast.error(error.response?.data?.detail || (language === 'ar' ? 'فشل إرسال الرمز' : 'Failed to send code'));
    } finally {
      setLoading(false);
    }
  };

  // Step 4: Verify phone code
  const verifyPhoneCode = async () => {
    if (formData.phoneCode.length !== 6) {
      toast.error(language === 'ar' ? 'يرجى إدخال الرمز المكون من 6 أرقام' : 'Please enter the 6-digit code');
      return;
    }
    setLoading(true);
    try {
      const response = await axios.post(`${BACKEND_URL}/api/auth/forgot-password/verify-phone`, {
        email: formData.email,
        code: formData.phoneCode
      });
      if (response.data.verified) {
        toast.success(language === 'ar' ? 'تم التحقق من رقم الهاتف' : 'Phone verified successfully');
        setStep(5);
      } else {
        toast.error(language === 'ar' ? 'رمز التحقق غير صحيح' : 'Invalid verification code');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || (language === 'ar' ? 'رمز التحقق غير صحيح' : 'Invalid verification code'));
    } finally {
      setLoading(false);
    }
  };

  // Step 5: Reset password
  const resetPassword = async () => {
    if (!isPasswordValid) {
      toast.error(language === 'ar' ? 'كلمة المرور لا تستوفي المتطلبات' : 'Password does not meet requirements');
      return;
    }
    if (!doPasswordsMatch) {
      toast.error(language === 'ar' ? 'كلمات المرور غير متطابقة' : 'Passwords do not match');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${BACKEND_URL}/api/auth/forgot-password/reset-password`, {
        email: formData.email,
        new_password: formData.newPassword
      });
      toast.success(language === 'ar' ? 'تم تغيير كلمة المرور بنجاح!' : 'Password changed successfully!');
      navigate('/login');
    } catch (error) {
      toast.error(error.response?.data?.detail || (language === 'ar' ? 'فشل تغيير كلمة المرور' : 'Failed to reset password'));
    } finally {
      setLoading(false);
    }
  };

  const OtpInputs = ({ value, type }) => (
    <div className="flex justify-center gap-2 sm:gap-3" dir="ltr" onPaste={(e) => handleOtpPaste(e, type)}>
      {[0, 1, 2, 3, 4, 5].map((index) => (
        <input
          key={index}
          ref={(el) => {
            if (type === 'email') emailOtpRefs.current[index] = el;
            else phoneOtpRefs.current[index] = el;
          }}
          type="text"
          inputMode="numeric"
          maxLength={1}
          value={value[index] || ''}
          onChange={(e) => handleOtpChange(index, e.target.value, type)}
          onKeyDown={(e) => handleOtpKeyDown(index, e, type)}
          onFocus={(e) => e.target.select()}
          autoComplete="one-time-code"
          className={`w-10 h-12 sm:w-12 sm:h-14 text-center text-xl sm:text-2xl font-bold border-2 rounded-lg focus:border-[#38BDF8] focus:ring-2 focus:ring-[#38BDF8]/20 outline-none transition-all ${
            isDarkMode 
              ? 'bg-[#1E293B] border-[#334155] text-white' 
              : 'bg-white border-gray-200 text-gray-900'
          }`}
          data-testid={`${type}-otp-${index}`}
        />
      ))}
    </div>
  );

  return (
    <div className={`min-h-screen flex items-center justify-center px-4 py-12 ${isDarkMode ? 'bg-[#0B0F19]' : 'bg-gradient-to-br from-gray-100 to-gray-200'}`}>
      <div className="max-w-md w-full">
        {/* Back Button */}
        <button
          onClick={() => step > 1 ? setStep(step - 1) : navigate('/login')}
          className={`mb-6 flex items-center gap-2 transition-colors ${isDarkMode ? 'text-gray-400 hover:text-white' : 'text-gray-600 hover:text-gray-900'}`}
          data-testid="back-btn"
        >
          <ArrowLeft className={`h-5 w-5 ${language === 'en' ? '' : 'rotate-180'}`} />
          <span>{language === 'ar' ? 'رجوع' : 'Back'}</span>
        </button>

        <div className={`rounded-2xl shadow-xl p-6 sm:p-8 ${isDarkMode ? 'bg-[#131B2C]' : 'bg-white'}`}>
          {/* Step 1: Enter Email */}
          {step === 1 && (
            <div className="space-y-6" data-testid="step-1">
              <div className="text-center">
                <div className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 ${isDarkMode ? 'bg-[#38BDF8]/20' : 'bg-[#1E9BD9]/10'}`}>
                  <KeyRound className="h-8 w-8 text-[#38BDF8]" />
                </div>
                <h1 className={`text-2xl font-bold font-heading mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                  {language === 'ar' ? 'نسيت كلمة المرور؟' : 'Forgot Password?'}
                </h1>
                <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  {language === 'ar' ? 'أدخل بريدك الإلكتروني لإعادة تعيين كلمة المرور' : 'Enter your email to reset your password'}
                </p>
              </div>

              <div className="space-y-4">
                <div className="relative">
                  <Mail className={`absolute ${language === 'ar' ? 'right-3' : 'left-3'} top-1/2 -translate-y-1/2 h-5 w-5 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`} />
                  <Input
                    type="email"
                    placeholder={language === 'ar' ? 'أدخل بريدك الإلكتروني' : 'Enter your email'}
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className={`${language === 'ar' ? 'pr-10' : 'pl-10'} py-6 ${
                      isDarkMode 
                        ? 'bg-[#1E293B] border-[#334155] text-white placeholder:text-gray-500' 
                        : 'bg-white border-gray-200 text-gray-900 placeholder:text-gray-400'
                    }`}
                    dir="ltr"
                    data-testid="email-input"
                  />
                </div>

                <Button
                  onClick={sendEmailCode}
                  disabled={loading || !formData.email}
                  className="w-full bg-[#38BDF8] hover:bg-[#0EA5E9] text-white py-6 text-lg font-semibold"
                  data-testid="send-email-btn"
                >
                  {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : (language === 'ar' ? 'إرسال رمز التحقق' : 'Send Verification Code')}
                </Button>
              </div>

              <div className="text-center pt-4">
                <Link to="/login" className="text-[#38BDF8] hover:underline text-sm">
                  {language === 'ar' ? 'العودة لتسجيل الدخول' : 'Back to Login'}
                </Link>
              </div>
            </div>
          )}

          {/* Step 2: Verify Email Code */}
          {step === 2 && (
            <div className="space-y-6" data-testid="step-2">
              <div className="text-center">
                <div className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 ${isDarkMode ? 'bg-[#38BDF8]/20' : 'bg-[#1E9BD9]/10'}`}>
                  <Mail className="h-8 w-8 text-[#38BDF8]" />
                </div>
                <h1 className={`text-2xl font-bold font-heading mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                  {language === 'ar' ? 'تحقق من بريدك' : 'Verify Your Email'}
                </h1>
                <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  {language === 'ar' ? 'أدخل الرمز المكون من 6 أرقام المرسل إلى' : 'Enter the 6-digit code sent to'}
                </p>
                <p className="text-[#38BDF8] font-medium" dir="ltr">{formData.email}</p>
              </div>

              <OtpInputs value={formData.emailCode} type="email" />

              <Button
                onClick={verifyEmailCode}
                disabled={loading || formData.emailCode.length !== 6}
                className="w-full bg-[#38BDF8] hover:bg-[#0EA5E9] text-white py-6 text-lg font-semibold"
                data-testid="verify-email-btn"
              >
                {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : (language === 'ar' ? 'تحقق' : 'Verify')}
              </Button>

              <button
                onClick={sendEmailCode}
                className="w-full text-[#38BDF8] hover:underline text-sm"
                disabled={loading}
              >
                {language === 'ar' ? 'إعادة إرسال الرمز' : 'Resend Code'}
              </button>
            </div>
          )}

          {/* Step 3: Phone Verification Notice */}
          {step === 3 && (
            <div className="space-y-6" data-testid="step-3">
              <div className="text-center">
                <div className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 ${isDarkMode ? 'bg-green-500/20' : 'bg-green-100'}`}>
                  <CheckCircle className={`h-8 w-8 ${isDarkMode ? 'text-green-400' : 'text-green-500'}`} />
                </div>
                <p className={`font-medium mb-4 ${isDarkMode ? 'text-green-400' : 'text-green-600'}`}>
                  {language === 'ar' ? 'تم التحقق من البريد الإلكتروني ✓' : 'Email Verified ✓'}
                </p>
                <h1 className={`text-2xl font-bold font-heading mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                  {language === 'ar' ? 'التحقق من الهاتف' : 'Phone Verification'}
                </h1>
                <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  {language === 'ar' ? 'سنرسل رمز تحقق إلى رقم الهاتف المسجل' : 'We will send a verification code to your registered phone'}
                </p>
                <p className="text-[#38BDF8] font-medium mt-2" dir="ltr">{maskedPhone}</p>
              </div>

              <Button
                onClick={sendPhoneCode}
                disabled={loading}
                className="w-full bg-[#38BDF8] hover:bg-[#0EA5E9] text-white py-6 text-lg font-semibold"
                data-testid="send-phone-btn"
              >
                {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : (language === 'ar' ? 'إرسال رمز التحقق' : 'Send Verification Code')}
              </Button>
            </div>
          )}

          {/* Step 4: Verify Phone Code */}
          {step === 4 && (
            <div className="space-y-6" data-testid="step-4">
              <div className="text-center">
                <div className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 ${isDarkMode ? 'bg-[#38BDF8]/20' : 'bg-[#1E9BD9]/10'}`}>
                  <Phone className="h-8 w-8 text-[#38BDF8]" />
                </div>
                <h1 className={`text-2xl font-bold font-heading mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                  {language === 'ar' ? 'تحقق من هاتفك' : 'Verify Your Phone'}
                </h1>
                <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  {language === 'ar' ? 'أدخل الرمز المكون من 6 أرقام المرسل إلى' : 'Enter the 6-digit code sent to'}
                </p>
                <p className="text-[#38BDF8] font-medium" dir="ltr">{maskedPhone}</p>
              </div>

              <OtpInputs value={formData.phoneCode} type="phone" />

              <Button
                onClick={verifyPhoneCode}
                disabled={loading || formData.phoneCode.length !== 6}
                className="w-full bg-[#38BDF8] hover:bg-[#0EA5E9] text-white py-6 text-lg font-semibold"
                data-testid="verify-phone-btn"
              >
                {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : (language === 'ar' ? 'تحقق' : 'Verify')}
              </Button>

              <button
                onClick={sendPhoneCode}
                className="w-full text-[#38BDF8] hover:underline text-sm"
                disabled={loading}
              >
                {language === 'ar' ? 'إعادة إرسال الرمز' : 'Resend Code'}
              </button>
            </div>
          )}

          {/* Step 5: Reset Password */}
          {step === 5 && (
            <div className="space-y-5" data-testid="step-5">
              <div className="text-center">
                <div className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 ${isDarkMode ? 'bg-green-500/20' : 'bg-green-100'}`}>
                  <CheckCircle className={`h-8 w-8 ${isDarkMode ? 'text-green-400' : 'text-green-500'}`} />
                </div>
                <p className={`font-medium mb-4 ${isDarkMode ? 'text-green-400' : 'text-green-600'}`}>
                  {language === 'ar' ? 'تم التحقق بنجاح ✓' : 'Verification Complete ✓'}
                </p>
                <h1 className={`text-2xl font-bold font-heading mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                  {language === 'ar' ? 'إعادة تعيين كلمة المرور' : 'Reset Password'}
                </h1>
                <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  {language === 'ar' ? 'أدخل كلمة المرور الجديدة' : 'Enter your new password'}
                </p>
              </div>

              <div className="space-y-4">
                <div>
                  <Label className={`text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    {language === 'ar' ? 'كلمة المرور الجديدة' : 'New Password'}
                  </Label>
                  <div className="relative mt-1">
                    <Lock className={`absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`} />
                    <Input
                      type={showPassword ? 'text' : 'password'}
                      placeholder={language === 'ar' ? 'أدخل كلمة المرور الجديدة' : 'Enter new password'}
                      value={formData.newPassword}
                      onChange={(e) => setFormData({ ...formData, newPassword: e.target.value })}
                      className={`pl-10 pr-10 py-5 ${
                        isDarkMode 
                          ? 'bg-[#1E293B] border-[#334155] text-white placeholder:text-gray-500' 
                          : 'bg-white border-gray-200 text-gray-900 placeholder:text-gray-400'
                      }`}
                      dir="ltr"
                      data-testid="new-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className={`absolute right-3 top-1/2 -translate-y-1/2 ${isDarkMode ? 'text-gray-500 hover:text-gray-300' : 'text-gray-400 hover:text-gray-600'}`}
                    >
                      {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                    </button>
                  </div>
                </div>

                <div>
                  <Label className={`text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    {language === 'ar' ? 'تأكيد كلمة المرور' : 'Confirm Password'}
                  </Label>
                  <div className="relative mt-1">
                    <Lock className={`absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`} />
                    <Input
                      type={showConfirmPassword ? 'text' : 'password'}
                      placeholder={language === 'ar' ? 'أعد إدخال كلمة المرور' : 'Re-enter password'}
                      value={formData.confirmPassword}
                      onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                      className={`pl-10 pr-10 py-5 ${
                        isDarkMode 
                          ? 'bg-[#1E293B] border-[#334155] text-white placeholder:text-gray-500' 
                          : 'bg-white border-gray-200 text-gray-900 placeholder:text-gray-400'
                      }`}
                      dir="ltr"
                      data-testid="confirm-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className={`absolute right-3 top-1/2 -translate-y-1/2 ${isDarkMode ? 'text-gray-500 hover:text-gray-300' : 'text-gray-400 hover:text-gray-600'}`}
                    >
                      {showConfirmPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                    </button>
                  </div>
                  {formData.confirmPassword && !doPasswordsMatch && (
                    <p className="text-red-500 text-xs mt-1">
                      {language === 'ar' ? 'كلمات المرور غير متطابقة' : 'Passwords do not match'}
                    </p>
                  )}
                </div>

                {/* Password Requirements */}
                <div className={`rounded-lg p-3 ${isDarkMode ? 'bg-[#1E293B]' : 'bg-gray-50'}`}>
                  <p className={`text-xs font-medium mb-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                    {language === 'ar' ? 'متطلبات كلمة المرور:' : 'Password Requirements:'}
                  </p>
                  <div className="grid grid-cols-1 gap-1.5 text-xs">
                    <div className={`flex items-center gap-2 ${passwordRequirements.minLength ? 'text-green-500' : isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                      {passwordRequirements.minLength ? <Check className="h-3.5 w-3.5" /> : <X className="h-3.5 w-3.5" />}
                      <span>{language === 'ar' ? '10 أحرف على الأقل' : 'At least 10 characters'}</span>
                    </div>
                    <div className={`flex items-center gap-2 ${passwordRequirements.hasUppercase ? 'text-green-500' : isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                      {passwordRequirements.hasUppercase ? <Check className="h-3.5 w-3.5" /> : <X className="h-3.5 w-3.5" />}
                      <span>{language === 'ar' ? 'حرف كبير واحد (A-Z)' : 'One uppercase letter (A-Z)'}</span>
                    </div>
                    <div className={`flex items-center gap-2 ${passwordRequirements.hasLowercase ? 'text-green-500' : isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                      {passwordRequirements.hasLowercase ? <Check className="h-3.5 w-3.5" /> : <X className="h-3.5 w-3.5" />}
                      <span>{language === 'ar' ? 'حرف صغير واحد (a-z)' : 'One lowercase letter (a-z)'}</span>
                    </div>
                    <div className={`flex items-center gap-2 ${passwordRequirements.hasNumber ? 'text-green-500' : isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                      {passwordRequirements.hasNumber ? <Check className="h-3.5 w-3.5" /> : <X className="h-3.5 w-3.5" />}
                      <span>{language === 'ar' ? 'رقم واحد (0-9)' : 'One number (0-9)'}</span>
                    </div>
                    <div className={`flex items-center gap-2 ${passwordRequirements.hasSymbol ? 'text-green-500' : isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                      {passwordRequirements.hasSymbol ? <Check className="h-3.5 w-3.5" /> : <X className="h-3.5 w-3.5" />}
                      <span>{language === 'ar' ? 'رمز واحد (!@#$%...)' : 'One symbol (!@#$%...)'}</span>
                    </div>
                  </div>
                </div>
              </div>

              <Button
                onClick={resetPassword}
                disabled={loading || !isPasswordValid || !doPasswordsMatch}
                className="w-full bg-[#38BDF8] hover:bg-[#0EA5E9] text-white py-6 text-lg font-bold disabled:opacity-50"
                data-testid="reset-password-btn"
              >
                {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : (language === 'ar' ? 'تغيير كلمة المرور' : 'Reset Password')}
              </Button>
            </div>
          )}
        </div>

        {/* Step Indicator */}
        <div className="mt-6 flex justify-center gap-2">
          {[1, 2, 3, 4, 5].map((s) => (
            <div
              key={s}
              className={`w-2 h-2 rounded-full transition-all ${
                s === step ? 'w-6 bg-[#38BDF8]' : s < step ? 'bg-green-500' : isDarkMode ? 'bg-gray-700' : 'bg-gray-300'
              }`}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
